import axios from 'axios';

// Configuration
const USE_MOCK_DATA = true; // Always use mock data since API is giving 404
const API_BASE_URL = 'https://rumahakrilik.id/api';
const STORAGE_KEY = 'rnd_projects';

// Log error function to handle API errors consistently
const logApiError = (error, context) => {
  console.error(`RnD Service Error (${context}):`, error);
  if (error.response) {
    console.log('Response status:', error.response.status);
    console.log('Response data:', error.response.data);
  }
};

// Create a mock storage in localStorage
const getProjectsFromStorage = () => {
  try {
    const projects = localStorage.getItem(STORAGE_KEY);
    return projects ? JSON.parse(projects) : [];
  } catch (e) {
    console.error('Error retrieving projects from localStorage:', e);
    return [];
  }
};

const saveProjectsToStorage = (projects) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  } catch (e) {
    console.error('Error saving projects to localStorage:', e);
  }
};

// Initialize with sample data if no data exists
const initializeMockData = () => {
  const sampleProjects = [
    {
      id: 1,
      name: "Display Akrilik Premium",
      description: "Pengembangan display akrilik premium dengan LED",
      start_date: "2025-04-01",
      target_date: "2025-06-30",
      status: "in_progress",
      phase: "research",
      progress: {
        research: true,
        production: false,
        photo: false,
        video: false,
        finished: false
      },
      manager: "Muhammad Botee",
      priority: "high"
    },
    {
      id: 2,
      name: "Box Akrilik Anti Gores",
      description: "Pengembangan material akrilik tahan gores untuk box",
      start_date: "2025-03-15",
      target_date: "2025-05-30",
      status: "in_progress",
      phase: "production",
      progress: {
        research: true,
        production: true,
        photo: false,
        video: false,
        finished: false
      },
      manager: "Fatkhu Rozak",
      priority: "medium"
    },
    {
      id: 3,
      name: "Ganci",
      description: "Pengembangan ganci akrilik custom dengan teknik laser",
      start_date: "2025-05-16",
      target_date: "2025-05-17",
      status: "in_progress",
      phase: "research",
      progress: {
        research: false,
        production: false,
        photo: false,
        video: false,
        finished: false
      },
      manager: "Andi",
      priority: "low"
    },
    {
      id: 4,
      name: "Jam Kabah",
      description: "Pengembangan jam dinding berbentuk kabah dengan akrilik",
      start_date: "2025-05-10",
      target_date: "2025-05-18",
      status: "finished",
      phase: "finished",
      progress: {
        research: true,
        production: true,
        photo: true,
        video: true,
        finished: true
      },
      end_date: "2025-05-18",
      manager: "Fatkhu Rozak",
      priority: "high"
    }
  ];
  
  saveProjectsToStorage(sampleProjects);
  return sampleProjects;
};

// Check for existing projects or create new ones
const ensureMockData = () => {
  const projects = getProjectsFromStorage();
  if (!projects || projects.length === 0) {
    return initializeMockData();
  }
  return projects;
};

// Don't try API if we know it doesn't exist
// Set this to false to always use mock data and prevent unnecessary API calls
const API_EXISTS = false;

// Mock RnD Service
const mockRnDService = {
  // Get all projects
  getProjects: async () => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.get(`${API_BASE_URL}/rnd/projects/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log("API response:", response.data);
        return response.data;
      } catch (error) {
        logApiError(error, 'getProjects');
      }
    }
    
    // Always use mock data for now
    return ensureMockData();
  },

  // Get active projects
  getActiveProjects: async () => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.get(`${API_BASE_URL}/rnd/projects/active/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
      } catch (error) {
        logApiError(error, 'getActiveProjects');
      }
    }
    
    const projects = ensureMockData();
    return projects.filter(p => p.status !== 'finished');
  },

  // Get finished projects
  getFinishedProjects: async () => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.get(`${API_BASE_URL}/rnd/projects/finished/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
      } catch (error) {
        logApiError(error, 'getFinishedProjects');
      }
    }
    
    const projects = ensureMockData();
    return projects.filter(p => p.status === 'finished');
  },

  // Get a specific project by ID
  getProject: async (id) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.get(`${API_BASE_URL}/rnd/projects/${id}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
      } catch (error) {
        logApiError(error, `getProject(${id})`);
      }
    }
    
    const projects = ensureMockData();
    const project = projects.find(p => p.id === parseInt(id));
    if (!project) throw new Error('Project not found');
    return project;
  },

  // Create a new project
  createProject: async (projectData) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.post(`${API_BASE_URL}/rnd/projects/`, projectData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        logApiError(error, 'createProject');
      }
    }
    
    // Use mock data
    const projects = ensureMockData();
    const newId = projects.length > 0 ? Math.max(...projects.map(p => p.id)) + 1 : 1;
    
    const newProject = {
      id: newId,
      ...projectData,
      status: 'in_progress',
      phase: 'research',
      progress: {
        research: false,
        production: false,
        photo: false,
        video: false,
        finished: false
      },
      created_at: new Date().toISOString()
    };
    
    projects.push(newProject);
    saveProjectsToStorage(projects);
    return newProject;
  },

  // Update a project
  updateProject: async (id, projectData) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.put(`${API_BASE_URL}/rnd/projects/${id}/`, projectData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        logApiError(error, `updateProject(${id})`);
      }
    }
    
    // Use mock data
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    
    if (index === -1) throw new Error('Project not found');
    
    projects[index] = {
      ...projects[index],
      ...projectData,
      updated_at: new Date().toISOString()
    };
    
    saveProjectsToStorage(projects);
    return projects[index];
  },

  // Delete a project
  deleteProject: async (id) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        await axios.delete(`${API_BASE_URL}/rnd/projects/${id}/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return { success: true };
      } catch (error) {
        logApiError(error, `deleteProject(${id})`);
      }
    }
    
    // Use mock data
    const projects = getProjectsFromStorage().filter(p => p.id !== parseInt(id));
    saveProjectsToStorage(projects);
    return { success: true };
  },

  // Updated updateProjectProgress to support enhanced features
  updateProjectProgress: async (id, projectData) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        const response = await axios.post(`${API_BASE_URL}/rnd/projects/${id}/progress/`, projectData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        return response.data;
      } catch (error) {
        logApiError(error, `updateProjectProgress(${id})`);
      }
    }
    
    // Use mock data
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    if (index === -1) throw new Error('Project not found');
    
    // Update progress
    projects[index].progress = {
      ...projects[index].progress,
      ...projectData.progress
    };
    
    // Update phase based on progress
    if (projectData.progress.finished) {
      projects[index].phase = 'finished';
      projects[index].status = 'finished';
      projects[index].end_date = new Date().toISOString().split('T')[0];
    } else if (projectData.progress.video) {
      projects[index].phase = 'video';
    } else if (projectData.progress.photo) {
      projects[index].phase = 'photo';
    } else if (projectData.progress.production) {
      projects[index].phase = 'production';
    } else if (projectData.progress.research) {
      projects[index].phase = 'research';
    }
    
    // Store extended tracking data
    if (projectData.milestones) projects[index].milestones = projectData.milestones;
    if (projectData.tasks) projects[index].tasks = projectData.tasks;
    if (projectData.attachments) projects[index].attachments = projectData.attachments;
    if (projectData.comments) projects[index].comments = projectData.comments;
    if (projectData.progressHistory) projects[index].progressHistory = projectData.progressHistory;
    if (projectData.riskAlerts) projects[index].riskAlerts = projectData.riskAlerts;
    
    // Update last modified timestamp
    projects[index].updated_at = new Date().toISOString();
    
    saveProjectsToStorage(projects);
    return projects[index];
  },

  // Add file attachment to project
  addProjectAttachment: async (id, fileData) => {
    // In a real app, this would upload the file to a server
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    if (index === -1) throw new Error('Project not found');
    
    if (!projects[index].attachments) {
      projects[index].attachments = [];
    }
    
    // Create a new attachment record
    const attachment = {
      id: Date.now().toString(),
      name: fileData.name,
      description: fileData.description,
      size: fileData.file ? fileData.file.size : 0,
      type: fileData.file ? fileData.file.type : 'unknown',
      uploadedAt: new Date().toISOString(),
      uploadedBy: localStorage.getItem('username') || 'User'
    };
    
    projects[index].attachments.push(attachment);
    saveProjectsToStorage(projects);
    
    return attachment;
  },

  // Add task to project
  addProjectTask: async (id, taskData) => {
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    if (index === -1) throw new Error('Project not found');
    
    if (!projects[index].tasks) {
      projects[index].tasks = [];
    }
    
    const task = {
      ...taskData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
      createdBy: localStorage.getItem('username') || 'User'
    };
    
    projects[index].tasks.push(task);
    saveProjectsToStorage(projects);
    
    return task;
  },

  // Add milestone to project
  addProjectMilestone: async (id, milestoneData) => {
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    if (index === -1) throw new Error('Project not found');
    
    if (!projects[index].milestones) {
      projects[index].milestones = [];
    }
    
    projects[index].milestones.push(milestoneData);
    saveProjectsToStorage(projects);
    
    return milestoneData;
  },

  // Add comment to project
  addProjectComment: async (id, commentText) => {
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    if (index === -1) throw new Error('Project not found');
    
    if (!projects[index].comments) {
      projects[index].comments = [];
    }
    
    const comment = {
      id: Date.now().toString(),
      text: commentText,
      author: localStorage.getItem('username') || 'User',
      timestamp: new Date().toISOString()
    };
    
    projects[index].comments.push(comment);
    saveProjectsToStorage(projects);
    
    return comment;
  },

  // Mark project as deleted (soft delete)
  markProjectAsDeleted: async (id) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        await axios.post(`${API_BASE_URL}/rnd/projects/${id}/delete/`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return { success: true };
      } catch (error) {
        logApiError(error, `markProjectAsDeleted(${id})`);
      }
    }
    
    // Mock implementation using localStorage
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    
    if (index === -1) throw new Error('Project not found');
    
    projects[index] = {
      ...projects[index],
      deleted: true,
      deleted_at: new Date().toISOString().split('T')[0]
    };
    
    saveProjectsToStorage(projects);
    return { success: true };
  },

  // Restore deleted project
  restoreProject: async (id) => {
    if (!USE_MOCK_DATA && API_EXISTS) {
      try {
        const token = localStorage.getItem('jwtToken');
        await axios.post(`${API_BASE_URL}/rnd/projects/${id}/restore/`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return { success: true };
      } catch (error) {
        logApiError(error, `restoreProject(${id})`);
      }
    }
    
    // Mock implementation using localStorage
    const projects = ensureMockData();
    const index = projects.findIndex(p => p.id === parseInt(id));
    
    if (index === -1) throw new Error('Project not found');
    
    projects[index] = {
      ...projects[index],
      deleted: false,
      deleted_at: null
    };
    
    saveProjectsToStorage(projects);
    return { success: true };
  },
  
  // Force reset the mock data
  resetMockData: () => {
    return initializeMockData();
  },
  
  // Clear all mock data
  clearMockData: () => {
    localStorage.removeItem(STORAGE_KEY);
    return { success: true };
  }
};

// Ensure we have mock data on first load
ensureMockData();

export default mockRnDService;